import React, { useState } from 'react';
import { 
  Eye, 
  MessageSquare, 
  AlertTriangle, 
  CheckCircle, 
  X,
  Plus,
  Search
} from 'lucide-react';

const SupportTicket = () => {
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);

  const tickets = [
    { 
      id: '#1234', 
      company: 'Beauty Haven', 
      subject: 'Login Issue', 
      description: 'Users are unable to log in to the system. Getting authentication error.',
      priority: 'High', 
      status: 'Open', 
      date: '2025-01-15',
      assignedTo: 'Support Team',
      category: 'Technical',
      lastUpdated: '2025-01-15 14:30'
    },
    { 
      id: '#1235', 
      company: 'Style Studio', 
      subject: 'Payment Problem', 
      description: 'Credit card payment is being declined even with valid cards.',
      priority: 'Medium', 
      status: 'In Progress', 
      date: '2025-01-14',
      assignedTo: 'John Smith',
      category: 'Billing',
      lastUpdated: '2025-01-15 10:15'
    },
    { 
      id: '#1236', 
      company: 'Glamour Salon', 
      subject: 'Feature Request', 
      description: 'Would like to add bulk appointment scheduling feature for multiple staff.',
      priority: 'Low', 
      status: 'Open', 
      date: '2025-01-13',
      assignedTo: 'Product Team',
      category: 'Feature Request',
      lastUpdated: '2025-01-13 16:45'
    },
    { 
      id: '#1237', 
      company: 'Elegance Spa', 
      subject: 'Technical Support', 
      description: 'Mobile app is crashing when trying to view appointment calendar.',
      priority: 'High', 
      status: 'Open', 
      date: '2025-01-12',
      assignedTo: 'Support Team',
      category: 'Technical',
      lastUpdated: '2025-01-12 09:20'
    },
    { 
      id: '#1238', 
      company: 'Modern Cuts', 
      subject: 'Account Upgrade', 
      description: 'Need to upgrade from Starter to Professional package.',
      priority: 'Medium', 
      status: 'Resolved', 
      date: '2025-01-11',
      assignedTo: 'Sales Team',
      category: 'Account',
      lastUpdated: '2025-01-11 15:30'
    }
  ];

  const filteredTickets = tickets.filter(ticket => {
    const matchesPriority = selectedPriority === 'all' || ticket.priority.toLowerCase() === selectedPriority.toLowerCase();
    const matchesStatus = selectedStatus === 'all' || ticket.status.toLowerCase() === selectedStatus.toLowerCase();
    const matchesSearch = ticket.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesPriority && matchesStatus && matchesSearch;
  });

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category) => {
    switch (category.toLowerCase()) {
      case 'technical': return 'bg-purple-100 text-purple-800';
      case 'billing': return 'bg-orange-100 text-orange-800';
      case 'feature request': return 'bg-blue-100 text-blue-800';
      case 'account': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetails(true);
  };

  const handleNewTicket = () => {
    setShowNewTicketModal(true);
  };

  const openTickets = tickets.filter(t => t.status === 'Open' || t.status === 'In Progress').length;
  const highPriorityTickets = tickets.filter(t => t.priority === 'High').length;
  const resolvedTickets = tickets.filter(t => t.status === 'Resolved').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Support Tickets</h1>
        <button
          onClick={handleNewTicket}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>New Ticket</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Open Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{openTickets}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <MessageSquare className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Priority</p>
              <p className="text-2xl font-bold text-gray-900">{highPriorityTickets}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">{resolvedTickets}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="text-sm text-gray-500">
          {filteredTickets.length} of {tickets.length} tickets
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">All Tickets</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {filteredTickets.map((ticket, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-sm font-medium text-gray-900">{ticket.id}</div>
                    <div>
                      <p className="font-medium text-gray-900">{ticket.company}</p>
                      <p className="text-sm text-gray-600">{ticket.subject}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(ticket.category)}`}>
                          {ticket.category}
                        </span>
                        <span className="text-xs text-gray-500">• {ticket.assignedTo}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                    <span className="text-sm text-gray-500">{ticket.date}</span>
                    <button 
                      onClick={() => handleViewTicket(ticket)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ticket Details Modal */}
      {showTicketDetails && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Ticket Details</h3>
              <button
                onClick={() => setShowTicketDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-600 text-sm">Ticket ID:</span>
                  <p className="font-medium">{selectedTicket.id}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Company:</span>
                  <p className="font-medium">{selectedTicket.company}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Subject:</span>
                  <p className="font-medium">{selectedTicket.subject}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Category:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedTicket.category)}`}>
                    {selectedTicket.category}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Priority:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Assigned To:</span>
                  <p className="font-medium">{selectedTicket.assignedTo}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Created:</span>
                  <p className="font-medium">{selectedTicket.date}</p>
                </div>
              </div>
              <div>
                <span className="text-gray-600 text-sm">Description:</span>
                <p className="mt-1 text-gray-800">{selectedTicket.description}</p>
              </div>
              <div>
                <span className="text-gray-600 text-sm">Last Updated:</span>
                <p className="font-medium">{selectedTicket.lastUpdated}</p>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowTicketDetails(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
              <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Reply
              </button>
              <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Mark Resolved
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Create New Ticket</h3>
              <button
                onClick={() => setShowNewTicketModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                  <option>Select a company</option>
                  <option>Beauty Haven</option>
                  <option>Style Studio</option>
                  <option>Glamour Salon</option>
                  <option>Elegance Spa</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                  <option>Technical</option>
                  <option>Billing</option>
                  <option>Feature Request</option>
                  <option>Account</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </div>
  <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"></textarea>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowNewTicketModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}
  </div>
);
};

export default SupportTicket;
